import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import {
  useDownloadPayslipEmployee,
  useGetPayslipDetail,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { paymentModels } from '@nxsys/constants/AppConstants';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { getReportedPension } from '@nxsys/utils/reported-pension';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { getIrelandTotalDeduction } from '../listing/columns';
import { Typography } from 'antd';

const EMPTY = '-';

function formatValue(
  value: string | number | null | undefined,
  currencySymbol: string,
): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return formatMoney(value, currencySymbol);
  }
  return String(value);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const d = moment(value).tz('Europe/London');
  return d.isValid() ? d.format('DD/MM/YYYY') : EMPTY;
}

const PayslipDetail: React.FC = () => {
  const { messages } = useTypedIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigateWithQuery();

  const { user, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const subcontractorId = user?.subcontractorId?.id ?? '';

  const paymentModel =
    user?.subcontractorId?.setting?.paymentModel ||
    user?.subcontractorId?.paymentModel;

  const isCIS = isCISPaymentModel(paymentModel);
  const isIreland = payrollCountry === IRELAND;

  const { run: downloadPayslipEmployee, loading: isDownloadPayslipPending } =
    useDownloadPayslipEmployee();
  const {
    value: detailValue,
    run: getPayslipDetail,
    loading: isDetailLoading,
  } = useGetPayslipDetail();

  // API returns the payslip object at root
  const record = useMemo(
    () => (detailValue && typeof detailValue === 'object' ? detailValue : null),
    [detailValue],
  );

  useEffect(() => {
    if (!id) return;
    getPayslipDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when id changes; getPayslipDetail (run) is a new ref each render and would cause an infinite loop
  }, [id]);

  const handleGoBack = useCallback(() => {
    navigate('/payslip');
  }, [navigate]);

  const handleDownload = useCallback(async () => {
    if (!id) return;
    try {
      const res = await downloadPayslipEmployee(id);
      if (res?.data) {
        window.open(res.data, '_blank');
      } else {
        // NX-5866: see Payslip.tsx — a refusal arrives as HTTP 200.
        showMessage({
          type: 'error',
          content: res?.error
            ? getErrorMessage(res.error)
            : messages['message.somethingWentWrong'],
        });
      }
    } catch {
      // Error handled by hook
    }
  }, [id, downloadPayslipEmployee, messages]);

  const renderTitle = useCallback(
    () => (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={
            isCIS
              ? messages['common.selfBillDetail']
              : messages['common.payslipDetail']
          }
          separator=""
          title=""
        />
      </div>
    ),
    [messages, handleGoBack],
  );

  const renderRightContent = useCallback(
    () => (
      <AppButton
        type="primary"
        name={messages['common.download'] ?? 'Download'}
        leftIcon={<Icons.IconDownload color="white" />}
        onClick={handleDownload}
        loading={isDownloadPayslipPending}
      />
    ),
    [messages, handleDownload, isDownloadPayslipPending],
  );

  const currencySymbol = showCurrencyByCountry(payrollCountry);
  const pr = record?.payrollRef;
  const puv = pr?.payeeUmbrellaValue;
  const irl = pr?.irelandValue as Record<string, any> | undefined;

  const leftColumnItems = useMemo(() => {
    if (!record) return [];
    return [
      {
        label: isCIS
          ? (messages['common.selfBillId'] ?? 'Self Bill ID')
          : (messages['common.payslipsId'] ?? 'Payslips ID'),
        value: isCIS
          ? (record.id || EMPTY)
          : ((record.id || '').replace('SFB', 'PAY') || EMPTY),
      },
      {
        label: messages['common.taxPeriod'] ?? 'Tax Period',
        value:
          record.taxPeriod != null && record.taxYear != null
            ? `${record.taxPeriod}/${record.taxYear ?? record.currentYear ?? ''}`
            : EMPTY,
      },
      {
        label: messages['common.paymentModel'] ?? 'Payment Model',
        value: pr?.paymentModel
          ? (paymentModels[pr.paymentModel as keyof typeof paymentModels] ??
            pr.paymentModel)
          : EMPTY,
      },
      {
        label: messages['common.paymentCompany'] ?? 'Payment Company',
        value: record.companyRef?.companyName ?? EMPTY,
      },
      ...(!isIreland
        ? [
            {
              label: messages['common.agencyName'] ?? 'Agency Name',
              value: pr?.agencyName ?? EMPTY,
            },
          ]
        : []),
      {
        label: isIreland
          ? (messages['common.grossPay'] || 'Gross Pay')
          : (messages['common.timesheetValue'] ?? 'Timesheet Value'),
        value: formatValue(record.timesheetValue, currencySymbol),
      },
      ...(!isCIS
        ? [
            {
              label: messages['common.hours'] ?? 'Hours',
              value: puv?.totalHours != null ? String(puv.totalHours) : EMPTY,
            },
            ...(!isIreland
              ? [
                  {
                    label: messages['common.margin'] ?? 'Margin',
                    value: formatValue(record.margin, currencySymbol),
                  },
                  {
                    label:
                      messages['common.apprenticeLevy'] ?? 'Apprentice Levy',
                    value: formatValue(puv?.appLevy, currencySymbol),
                  },
                ]
              : []),
            {
              label: messages['common.salary'] ?? 'Salary',
              value: formatValue(pr?.salary, currencySymbol),
            },
            {
              label:
                messages['common.advancedHolidayPay'] ??
                'Advanced Holiday Pay',
              value: formatValue(
                isIreland ? irl?.holidayPayAdvanced : puv?.holidayPay,
                currencySymbol,
              ),
            },
            {
              label: messages['common.holidayPaid'] ?? 'Holiday Paid',
              value: formatValue(pr?.holidayPaid, currencySymbol),
            },
          ]
        : []),
      {
        label: messages['common.totalDeduction'] ?? 'Total Deduction',
        value: formatValue(
          isIreland ? getIrelandTotalDeduction(pr) : pr?.totalDeduction,
          currencySymbol,
        ),
      },
      {
        label: messages['common.netPay'] ?? 'Net Pay',
        value: formatValue(record.netPay ?? pr?.netPay, currencySymbol),
      },
      ...(!isCIS
        ? [
            ...(!isIreland
              ? [
                  {
                    label: messages['common.studentLoan'] ?? 'Student Loan',
                    value: formatValue(pr?.studentLoan, currencySymbol),
                  },
                  {
                    label:
                      messages['common.pgStudentLoan'] ?? 'PG Student Loan',
                    value: formatValue(pr?.postgraduateLoan, currencySymbol),
                  },
                  {
                    label: messages['common.attachment'] ?? 'Attachment',
                    value: formatValue(
                      puv?.attachmentOrderDeducted,
                      currencySymbol,
                    ),
                  },
                  {
                    label: 'AO Margin',
                    value: formatValue(
                      puv?.attachmentOrderMarginDeducted,
                      currencySymbol,
                    ),
                  },
                  {
                    label: messages['common.sickPaySSP'] ?? 'Sick Pay (SSP)',
                    value: formatValue(puv?.ssp, currencySymbol),
                  },
                  {
                    label:
                      messages['common.maternityPaySMP'] ??
                      'Maternity Pay (SMP)',
                    value: formatValue(puv?.smp, currencySymbol),
                  },
                  {
                    label:
                      messages['common.adoptionPaySAP'] ??
                      'Adoption Pay (SAP)',
                    value: formatValue(puv?.sap, currencySymbol),
                  },
                  {
                    label:
                      messages['common.paternityPaySPP'] ?? 'Paternity (SPP)',
                    value: formatValue(
                      puv?.spp ?? puv?.sppa,
                      currencySymbol,
                    ),
                  },
                ]
              : [
                  {
                    label: 'USCable Gross',
                    value: formatValue(irl?.uscGross, currencySymbol),
                  },
                  {
                    label: 'USC',
                    value: formatValue(irl?.usc, currencySymbol),
                  },
                  {
                    label: 'Employee PRSIable Gross',
                    value: formatValue(irl?.eePrsiGross, currencySymbol),
                  },
                  {
                    label: 'Employee PRSI',
                    value: formatValue(irl?.eePRSI, currencySymbol),
                  },
                  {
                    label: 'Employer PRSIable Gross',
                    value: formatValue(irl?.erPrsiGross, currencySymbol),
                  },
                  {
                    label: 'Employer PRSI',
                    value: formatValue(irl?.erPRSI, currencySymbol),
                  },
                  {
                    label: messages['common.sickPaySSP'] ?? 'Sick Pay (SSP)',
                    value: formatValue(puv?.ssp, currencySymbol),
                  },
                ]),
          ]
        : [
            {
              label: messages['common.cisDeduction'] ?? 'CIS Deduction',
              value: formatValue(record.cisDeduction, currencySymbol),
            },
            {
              label: messages['common.vatAmount'] ?? 'VAT',
              value: formatValue(record.vat, currencySymbol),
            },
            {
              label: messages['common.materials'] ?? 'Materials',
              value: formatValue(record.materials, currencySymbol),
            },
          ]),
    ];
  }, [record, currencySymbol, messages, isCIS, isIreland]);

  const rightColumnItems = useMemo(() => {
    if (!record) return [];
    const reportedPension = getReportedPension(record.payrollRef);
    return [
      ...(!isCIS
        ? [
            {
              label: messages['common.expensesClaimed'] ?? 'Expenses Claimed',
              value: formatValue(pr?.expenseClaimed, currencySymbol),
            },
            {
              label: messages['common.expensePaid'] ?? 'Expense Paid',
              value: formatValue(puv?.expensesPaid, currencySymbol),
            },
            {
              label: messages['common.bonus'] ?? 'Bonus',
              value: formatValue(puv?.bonus, currencySymbol),
            },
          ]
        : []),
      {
        label: messages['common.otherPayment'] ?? 'Other Payment',
        value: formatValue(record.otherPayment, currencySymbol),
      },
      {
        label: messages['common.otherDeduction'] ?? 'Other Deduction',
        value: formatValue(record.otherDeduction, currencySymbol),
      },
      {
        label: messages['common.totalPayment'] ?? 'Total Payment',
        value: formatValue(pr?.totalPayment, currencySymbol),
      },
      {
        label: messages['common.taxDeducted'] ?? 'Tax Deducted',
        value: formatValue(puv?.tax, currencySymbol),
      },
      ...(!isCIS
        ? [
            ...(!isIreland
              ? [
                  {
                    label: messages['common.employeeNI'] ?? 'Employee NI',
                    value: formatValue(puv?.employeeNI, currencySymbol),
                  },
                  {
                    label: messages['common.employerNI'] ?? 'Employer NI',
                    value: formatValue(puv?.employersNI, currencySymbol),
                  },
                ]
              : [
                  {
                    label: 'MFF Gross',
                    value: formatValue(irl?.pensionGross, currencySymbol),
                  },
                ]),
            // Under salary sacrifice the employee pays no pension in law: the
            // deduction is salary given up, so it is labelled as such instead
            // of "Employee Pension".
            !isIreland && reportedPension.isSalarySacrifice
              ? {
                  label:
                    messages['common.salarySacrifice'] ?? 'Salary Sacrifice',
                  value: formatValue(
                    reportedPension.salarySacrificeAmount,
                    currencySymbol,
                  ),
                }
              : {
                  label: isIreland
                    ? 'Employee MFF'
                    : (messages['common.pensionEmpe'] ?? 'Employee Pension'),
                  value: isIreland
                    ? formatValue(irl?.eePension, currencySymbol)
                    : formatValue(
                        puv?.employeePension ||
                          (puv?.refundEEPension
                            ? -Math.abs(puv.refundEEPension)
                            : undefined),
                        currencySymbol,
                      ),
                },
            ...(isIreland
              ? [
                  {
                    label: 'Employer MFF',
                    value: formatValue(irl?.erPension, currencySymbol),
                  },
                  {
                    label: messages['common.pensionEmpe'] ?? 'Employee Pension',
                    value: formatValue(puv?.employeePension, currencySymbol),
                  },
                  {
                    label: messages['common.pensionEmpr'] ?? 'Employer Pension',
                    value: formatValue(puv?.employersPension, currencySymbol),
                  },
                  {
                    label: messages['common.avcEmployee'] ?? 'AVC Employee',
                    value: formatValue(puv?.avcEEPension, currencySymbol),
                  },
                  {
                    label: messages['common.avcEmployer'] ?? 'AVC Employer',
                    value: formatValue(puv?.avcERPension, currencySymbol),
                  },
                  {
                    label: 'LPT',
                    value: formatValue(irl?.lptDeducted, currencySymbol),
                  },
                ]
              : []),
            {
              label: messages['common.holidayRetained'] ?? 'Holiday Retained',
              value: formatValue(pr?.retainedHolidayPay, currencySymbol),
            },
            ...(!isIreland
              ? [
                  {
                    label: messages['common.additionalSPP'] ?? 'Additional SPP',
                    value: formatValue(puv?.sppa, currencySymbol),
                  },
                ]
              : []),
          ]
        : []),
      {
        label: messages['common.grossForTaxYTD'] ?? 'Gross For Tax (YTD)',
        value: formatValue(puv?.grossForTaxTD ?? puv?.taxYTD, currencySymbol),
      },
      {
        label: messages['common.taxYTD'] ?? 'Tax (YTD)',
        value: formatValue(puv?.taxTD, currencySymbol),
      },
      {
        label: messages['common.paymentDate'] ?? 'Payment Date',
        value: formatDate(pr?.paymentDate),
      },
      {
        label: messages['common.paid'] ?? 'Paid',
        value: formatValue(pr?.paid, currencySymbol),
      },
      {
        label: messages['common.outstanding'] ?? 'Outstanding',
        value: formatValue(pr?.outstanding, currencySymbol),
      },
      ...(!isCIS && !isIreland
        ? [
            {
              label: messages['common.fpsAmount'] ?? 'FPS Amount',
              value: formatValue(record.fpsAmount, currencySymbol),
            },
            {
              label: messages['common.grossForTax'] ?? 'Gross For Tax',
              value: formatValue(
                puv?.grossForTaxAndNI ?? puv?.grossForTax,
                currencySymbol,
              ),
            },
            {
              label: messages['common.grossForNI'] ?? 'Gross For NI',
              value: formatValue(
                puv?.grossForTaxAndNI ?? puv?.grossForNI,
                currencySymbol,
              ),
            },
          ]
        : []),
    ];
  }, [record, currencySymbol, messages, isCIS, isIreland]);

  if (!subcontractorId) {
    return (
      <AppsContainer fullView type="bottom" title={renderTitle()}>
        <AppCard>
          <AppsContent>
            <Typography.Text>
              {messages['payslip.empty'] ?? 'No payslips available.'}
            </Typography.Text>
          </AppsContent>
        </AppCard>
      </AppsContainer>
    );
  }

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={isDetailLoading || isDownloadPayslipPending}
    >
      {!record && !isDetailLoading && id ? (
        <AppCard>
          <AppsContent>
            <Typography.Text>
              {messages['payslip.empty'] ?? 'Payslip not found.'}
            </Typography.Text>
          </AppsContent>
        </AppCard>
      ) : record ? (
        <AppCard>
          <AppsContent>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-1">
                <div className="flex flex-col">
                  {leftColumnItems.map((item, idx) => (
                    <div key={`left-${idx}`} className="flex  py-2">
                      <span className="text-gray-600 flex-1">{item.label}</span>
                      <Typography.Text className="flex-1">
                        {item.value}
                      </Typography.Text>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col">
                  {rightColumnItems.map((item, idx) => (
                    <div key={`right-${idx}`} className="flex  py-2">
                      <span className="text-gray-600 flex-1">{item.label}</span>
                      <Typography.Text className="flex-1">
                        {item.value}
                      </Typography.Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </AppsContent>
        </AppCard>
      ) : null}
    </AppsContainer>
  );
};

export default PayslipDetail;
